import React, { useEffect, useRef, useState } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

const Technologies = ({ technologies }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [clickedIndex, setClickedIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const containerRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState("800px");

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const visibleTechnologies = showAll ? technologies : technologies.slice(0, 6);

  useEffect(() => {
    if (containerRef.current) {
      setMaxHeight(showAll ? `${containerRef.current.scrollHeight}px` : "800px");
    }
  }, [showAll, visibleTechnologies.length]);

  return (
    <div className="mt-20 px-4">
      <h2 className="text-3xl md:text-4xl font-bold relative inline-block group [text-shadow:0_0_8px_rgba(59,130,246,0.8),0_0_16px_rgba(59,130,246,0.6)] pb-3">
        Technologies
        <span className="absolute left-0 bottom-0 h-1 w-full origin-right scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out bg-gradient-to-l from-blue-400 via-blue-500 to-blue-700 shadow-[0_4px_16px_0_rgba(59,130,246,0.7)] rounded-full" />
      </h2>

      {/* Technologies Grid */}
      <div
        className="overflow-hidden transition-all duration-700 ease-in-out"
        style={{ maxHeight }}
        ref={containerRef}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 bg-transparent w-full px-4 pt-5 pb-5">
          {visibleTechnologies.map((tech, index) => {
            const isActive = isMobile
              ? clickedIndex === index
              : hoveredIndex === index;

            return (
              <div
                key={index}
                className="relative bg-slate-950 border-2 border-blue-900 rounded-2xl hover:scale-105 hover:bg-slate-900 shadow-lg hover:shadow-blue-500/50 transition-all duration-300 p-5 text-white cursor-pointer overflow-hidden h-[150px]"
                onMouseEnter={() => !isMobile && setHoveredIndex(index)}
                onMouseLeave={() => !isMobile && setHoveredIndex(null)}
                onClick={() =>
                  isMobile &&
                  setClickedIndex(clickedIndex === index ? null : index)
                }
              >
                {/* Title Row */}
                <div className="flex items-center gap-4 mb-3">
                  {tech.icon}
                  <div>
                    <h4 className="text-lg font-semibold">{tech.name}</h4>
                    {!isActive && (
                      <span className="text-xs text-slate-300">
                        Experience Level
                      </span>
                    )}
                  </div>
                </div>

                {/* Animated Content */}
                <div className="relative h-[80px] transition-all duration-500 ease-in-out">
                  {/* Experience Bar */}
                  <div
                    className={`absolute inset-0 transition-all duration-500 ease-in-out transform ${
                      isActive
                        ? "-translate-y-6 opacity-0 pointer-events-none"
                        : "translate-y-0 opacity-100"
                    }`}
                  >
                    <div className="w-full bg-slate-800 h-3 rounded-lg overflow-hidden">
                      <div
                        className="h-full bg-green-400 transition-all duration-500"
                        style={{ width: `${tech.experience}%` }}
                      />
                    </div>
                    <div className="text-right text-sm mt-1 text-green-300">
                      {tech.experience}%
                    </div>
                  </div>

                  {/* Detail Slide */}
                  <div
                    className={`absolute inset-0 transition-all duration-500 ease-in-out transform ${
                      isActive
                        ? "translate-y-0 opacity-100"
                        : "translate-y-6 opacity-0 pointer-events-none"
                    }`}
                  >
                    <p className="text-sm text-blue-100">{tech.detail}</p>
                    <p className="text-xs mt-2 text-slate-400">
                      <span className="font-semibold text-white">Lib:</span>{" "}
                      {tech.lib}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Show More / Less Button */}
      {technologies.length > 6 && (
        <div className="text-center mt-8">
          <button
            onClick={() => {
              const currentY = window.scrollY;
              setShowAll((prev) => {
                const newState = !prev;
                setTimeout(() => {
                  window.scrollTo({
                    top: newState ? currentY + 500 : currentY - 600,
                    behavior: "smooth",
                  });
                }, 100); // wait for expand/collapse animation to trigger
                return newState;
              });
            }}
            className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium text-blue-300 bg-slate-800 hover:bg-blue-700 border border-blue-600 rounded-full shadow-md transition-all duration-300"
          >
            {showAll ? "Show Less" : "Show More"}
            {showAll ? <FaChevronUp /> : <FaChevronDown />}
          </button>
        </div>
      )}
    </div>
  );
};

export default Technologies;
